import { useEffect, useState, type FormEvent } from 'react';
import type { ProductVatCheck, VatCheckLevel, VatMode } from '@kassa/shared';
import { api, ApiError } from '../api';
import { VAT_LABEL } from '../format';
import { DataTable, PageHeader, useUrlFilters, type Column } from '../components/ListPage';
import { formatHs, VatCheckBadge } from '../components/VatCheckBadge';
import { searchNorm } from '../../../../packages/shared/src/search';

// «ƏDV yoxlaması» (KT-69, edv-qaydalari 2.8–2.9): XİF MN koduna görə ƏDV növü uyğun gəlməyən mallar.
// Mal avtomatik dəyişmir: ya kartda ƏDV növünü/kodu düzəlt, ya «Seçimim düzdür» (ƏDV-siz saxlayanda səbəb məcburidir, audit).
// Qırmızı və sarı həll olunmayınca alış keçirilmir (server 409). Süzgəc URL-də.
const TABS: { key: string; label: string; levels: VatCheckLevel[] }[] = [
  { key: '', label: 'Həll gözləyir', levels: ['red', 'yellow'] },
  { key: 'blue', label: 'Təsdiqlənmiş', levels: ['blue'] },
  { key: 'grey', label: 'Yoxlanmayan', levels: ['grey'] },
];
const KEYS = ['tab', 'q'] as const;
const errText = (e: unknown) => (e instanceof ApiError ? e.message : 'Xəta baş verdi');

/** «Seçimim düzdür» üçün mal: line — alış sətrindən (sətrin ƏDV növü kartdakından fərqli ola bilər). */
export interface AckTarget {
  productId: string;
  name: string;
  chosen: VatMode;
  suggested: VatMode | null;
  line?: boolean;
}

/** Səbəb məcburidir: malı ƏDV-siz (azad və ya 0%) saxlayırsınız, kod isə ƏDV-li deyir (server qaydası ilə eyni). */
const reasonRequired = (r: AckTarget) => (r.chosen === 'exempt' || r.chosen === 'vat0') && r.suggested === 'vat18';

export function VatCheck({ onOpen }: { onOpen: (productId: string) => void }) {
  const [f, setF] = useUrlFilters(KEYS);
  const tab = TABS.find((t) => t.key === f.tab) ?? TABS[0];
  const [rows, setRows] = useState<ProductVatCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acking, setAcking] = useState<ProductVatCheck | null>(null);
  const [seq, setSeq] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .vatCheckList(tab.levels)
      .then((r) => !cancelled && setRows(r))
      .catch((e: unknown) => !cancelled && setError(errText(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [tab.key, seq]);

  const n = searchNorm(f.q.trim());
  const shown = n ? rows.filter((r) => searchNorm(`${r.name} ${r.hsCode ?? ''}`).includes(n)) : rows;

  const columns: Column<ProductVatCheck>[] = [
    {
      key: 'name',
      label: 'Mal',
      render: (r) => (
        <button type="button" className="link-btn" onClick={() => onOpen(r.productId)}>
          {r.name}
        </button>
      ),
    },
    { key: 'hs', label: 'XİF MN', mono: true, render: (r) => (r.hsCode ? formatHs(r.hsCode) : '—') },
    { key: 'chosen', label: 'Seçilib', render: (r) => VAT_LABEL[r.chosen] },
    { key: 'sug', label: 'Koda görə', minor: true, render: (r) => (r.suggested ? VAT_LABEL[r.suggested] : '—') },
    { key: 'st', label: 'Status', render: (r) => <VatCheckBadge level={r.level} /> },
    {
      key: 'msg',
      label: 'Nə etməli',
      minor: true,
      render: (r) => (
        <span className="small">
          {r.title}
          {r.expiry && <span className="muted vat-check-note">{r.expiry}</span>}
          {r.legalBasis && <span className="muted vat-check-note">Əsas: {r.legalBasis}</span>}
        </span>
      ),
    },
    {
      key: 'act',
      label: '',
      render: (r) =>
        r.level === 'red' ? (
          <button type="button" className="btn-small" onClick={() => setAcking(r)}>
            Seçimim düzdür
          </button>
        ) : r.level === 'yellow' || (r.level === 'grey' && !r.hsCode) ? (
          <button type="button" className="btn-small" onClick={() => onOpen(r.productId)}>
            Kodu seç
          </button>
        ) : null,
    },
  ];

  return (
    <main className="list-page">
      <PageHeader
        title="ƏDV yoxlaması"
        sub={loading ? 'Yüklənir…' : `${shown.length} mal`}
        tabs={
          <div className="tabs" role="tablist" aria-label="Status">
            {TABS.map((t) => (
              <button key={t.key} type="button" role="tab" aria-selected={t.key === tab.key} className={t.key === tab.key ? 'tab active' : 'tab'} onClick={() => setF({ tab: t.key })}>
                {t.label}
              </button>
            ))}
          </div>
        }
      />
      <div className="list-toolbar">
        <label className="field">
          <span>Axtar</span>
          <input type="search" value={f.q} onChange={(e) => setF({ q: e.target.value })} placeholder="Mal adı və ya XİF MN" />
        </label>
      </div>
      <p className="muted small">Mal özü dəyişmir. Uyğun gəlməyəni kartda düzəldin və ya seçiminizi təsdiqləyin — həll olunmayınca alış sənədi keçirilmir.</p>
      <DataTable
        label="ƏDV yoxlaması"
        columns={columns}
        rows={shown}
        rowKey={(r) => r.productId}
        loading={loading}
        error={error}
        empty={tab.key === '' ? 'Uyğun gəlməyən mal yoxdur.' : 'Siyahı boşdur.'}
      />
      {acking && (
        <AckDialog
          row={acking}
          onCancel={() => setAcking(null)}
          onDone={() => {
            setAcking(null);
            setSeq((v) => v + 1);
          }}
        />
      )}
    </main>
  );
}

export function AckDialog({ row, onCancel, onDone }: { row: AckTarget; onCancel: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const need = reasonRequired(row);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.vatCheckAck(row.productId, reason.trim() || null, row.line ? row.chosen : undefined);
      onDone();
    } catch (err) {
      setError(errText(err));
      setBusy(false);
    }
  }

  return (
    <div className="modal-overlay" role="presentation">
      <form className="modal-box contract-form" role="dialog" aria-modal="true" aria-label="Seçimim düzdür" onSubmit={(e) => void submit(e)}>
        <h2>Seçimim düzdür</h2>
        <p>
          <strong>{row.name}</strong> — {VAT_LABEL[row.chosen]} saxlanılır{row.suggested ? `, kod isə «${VAT_LABEL[row.suggested]}» deyir` : ''}.
        </p>
        <p className="muted small">Təsdiq audit jurnalına yazılır. Kod və ya qayda dəyişsə, yenidən soruşulacaq.</p>
        <label className="field">
          <span>Səbəb{need ? ' *' : ''}</span>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={3} required={need} placeholder="məs. ixrac üçün — 0%" autoFocus />
        </label>
        {error && (
          <p className="bad" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={busy || (need && reason.trim().length < 3)}>
            Təsdiqləyirəm
          </button>
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Ləğv et
          </button>
        </div>
      </form>
    </div>
  );
}
